import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Heart } from "lucide-react";

// Import components
// NOTE: MobileNumberGate is parked for future use - uncomment when needed
// import MobileNumberGate from "./components/MobileNumberGate";
import NewHeroSection from "./components/NewHeroSection";
import BookShowcaseSection from "./components/BookShowcaseSection";
import CustomerDetailsModal from "./components/CustomerDetailsModal";
import DownloadPage from "./components/DownloadPage";

// Import services
import useNewRazorpay from "./hooks/useNewRazorpay";
import { booksData } from "./newMock";

// Analytics tracking helper
const trackEvent = async (event, data = {}) => {
  try {
    await fetch(`/api/analytics/${event}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  } catch (e) {
    console.log('Analytics tracking failed:', e);
  }
};

// Header component with Marriage101 logo
const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center">
      <div className="flex items-center space-x-2">
        <Heart className="w-8 h-8 text-red-500 fill-red-500" />
        <span className="text-xl font-bold text-gray-900">
          Marriage<span className="text-red-500">101</span>
        </span>
      </div>
    </div>
  </header>
);

const NewLandingPage = () => {
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [pageViewTracked, setPageViewTracked] = useState(false);
  
  const { initiatePayment, isProcessing } = useNewRazorpay();

  // Track landing page view on mount
  useEffect(() => {
    if (!pageViewTracked) {
      trackEvent('landing-page-view', { page: 'landing' });
      setPageViewTracked(true);
    }
  }, [pageViewTracked]);

  // NOTE: MobileNumberGate is parked for future use
  // To re-enable, uncomment the gate check and import MobileNumberGate component
  
  const handleViewSeries = () => {
    // Scroll to books section
    const booksSection = document.getElementById('books-section');
    if (booksSection) {
      booksSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePurchaseBook = (bookId) => {
    // Track payment initiation
    trackEvent('payment-initiated', { book_id: bookId, type: 'individual' });
    setSelectedPurchase({
      type: 'individual',
      bookIds: [bookId],
      amount: booksData.individual[bookId].price
    });
    setShowCustomerModal(true);
  };

  const handlePurchaseBundle = () => {
    // Track bundle payment initiation
    trackEvent('payment-initiated', { book_id: 'bundle', type: 'bundle' });
    setSelectedPurchase({
      type: 'bundle', 
      bookIds: Object.keys(booksData.individual),
      amount: booksData.bundle.price
    });
    setShowCustomerModal(true);
  };

  const handleCustomerSubmit = async (customerDetails) => {
    if (!selectedPurchase) return;

    try {
      const success = await initiatePayment(customerDetails, selectedPurchase);
      if (success) {
        setShowCustomerModal(false);
        setSelectedPurchase(null);
      }
    } catch (error) {
      toast.error("Payment initiation failed", {
        description: error.message,
      });
    }
  };

  const handleModalClose = () => {
    if (!isProcessing) {
      setShowCustomerModal(false);
      setSelectedPurchase(null);
    }
  };

  // Main landing page - mobile gate bypassed (parked for future use)
  return (
    <div className="App">
      <Header />
      <main className="pt-16">
        <NewHeroSection onViewSeriesClick={handleViewSeries} />
        
        <div id="books-section">
          <BookShowcaseSection 
            onPurchaseBook={handlePurchaseBook}
            onPurchaseBundle={handlePurchaseBundle}
          />
        </div>
      </main>

      {/* Customer Details Modal */}
      {showCustomerModal && (
        <CustomerDetailsModal
          isOpen={showCustomerModal}
          onClose={handleModalClose}
          onSubmit={handleCustomerSubmit}
          isProcessing={isProcessing}
          selectedPurchase={selectedPurchase}
        />
      )}
      
      <Toaster />
    </div>
  );
};

const DownloadPageComponent = () => {
  return <DownloadPage />;
};

function NewApp() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<NewLandingPage />} />
          <Route path="/download-page" element={<DownloadPageComponent />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default NewApp;